/**
 * CRM leads — the top of the funnel, before a lead becomes an opportunity.
 *
 * The one leads page: the older Sales > Leads copy is retired and
 * /app/sales/leads redirects here (CRM-W01). This view is organised around
 * qualification: where the lead came from, and whether anyone owns it.
 *
 * The stat cards read GET /v1/crm/leads/summary: every open lead, its value
 * summed by the API per currency (the workspace currency first, the others
 * beneath it, never added in). Each row shows its own currency, and a new
 * lead starts in the workspace currency (CUR-2).
 */
import { useState } from "react";
import {
  Button,
  Card,
  DataTable,
  EmptyState,
  Field,
  Input,
  PageHeader,
  Select,
  Skeleton,
  StatCard,
  StatusBadge,
} from "@/components/ui";
import { MoneyStatCard } from "@/components/Money";
import { QueryError } from "@/components/QueryError";
import { post, useApiList, useApiMutation, useApiQuery } from "@/lib/api/hooks";
import { currencyPickerOptions, useWorkspaceCurrency } from "@/lib/currency";
import { useTranslation } from "@/lib/i18n";
import { formatMoney } from "@/lib/money";
import { personName, type LeadRow, type LeadSummary } from "./types";

const SOURCES = [
  "Web",
  "Referral",
  "Event",
  "Outbound",
  "Partner",
  "Inbound call",
  "Other",
];

export function CrmLeadsPage() {
  const { t, i18n } = useTranslation();
  const { data, isLoading, error } = useApiList<LeadRow>(
    ["crm", "leads"],
    "/crm/leads",
  );
  const leadSummary = useApiQuery<LeadSummary>(
    ["crm", "leads", "summary"],
    "/crm/leads/summary",
  );
  const summary = leadSummary.data;
  const workspaceCurrency = useWorkspaceCurrency();
  const currencies = currencyPickerOptions(i18n.language, workspaceCurrency);
  const [creating, setCreating] = useState(false);
  const blank = () => ({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    companyName: "",
    source: "Web",
    estimatedValue: "",
    // What the API applies when nothing is sent (R3): the workspace's.
    currencyCode: workspaceCurrency ?? "",
  });
  const [form, setForm] = useState(blank);

  const create = useApiMutation<typeof form, unknown>(
    ["crm", "leads"],
    (input) =>
      post("/crm/leads", {
        lastName: input.lastName,
        firstName: input.firstName || undefined,
        email: input.email || undefined,
        phone: input.phone || undefined,
        companyName: input.companyName || undefined,
        source: input.source,
        estimatedValue: input.estimatedValue || undefined,
        currencyCode: input.currencyCode || undefined,
      }),
  );

  const rows = data?.items ?? [];

  return (
    <div>
      <PageHeader
        title="Leads"
        subtitle="Unqualified interest. A lead becomes an opportunity once there is a real deal to forecast."
        breadcrumb={["CRM", "Leads"]}
        actions={
          <Button
            variant="primary"
            onClick={() => {
              if (!creating) setForm(blank());
              setCreating((open) => !open);
            }}
          >
            {creating ? "Cancel" : "New lead"}
          </Button>
        }
      />

      {leadSummary.isError && (
        <QueryError
          error={leadSummary.error}
          retrying={leadSummary.isFetching}
          onRetry={() => void leadSummary.refetch()}
        />
      )}
      {summary && (
        <div className="atlas-grid atlas-grid--3 mb-4">
          <StatCard label="Open leads" value={summary.count} />
          <StatCard
            label="Unassigned"
            value={summary.unassigned}
            meta={
              summary.unassigned > 0
                ? "Nobody is following these up"
                : "Every lead has an owner"
            }
            tone={summary.unassigned > 0 ? "negative" : "positive"}
          />
          <MoneyStatCard
            label="Estimated value"
            figure={summary.estimatedValue}
          />
        </div>
      )}

      {creating && (
        <Card title="New lead" className="mb-4">
          <form
            onSubmit={(event) => {
              event.preventDefault();
              create.mutate(form, {
                onSuccess: () => {
                  setCreating(false);
                  setForm(blank());
                },
              });
            }}
          >
            <div className="atlas-grid atlas-grid--3">
              <Field label="First name">
                <Input
                  value={form.firstName}
                  onChange={(e) =>
                    setForm({ ...form, firstName: e.target.value })
                  }
                />
              </Field>
              <Field label="Last name" required>
                <Input
                  value={form.lastName}
                  required
                  onChange={(e) =>
                    setForm({ ...form, lastName: e.target.value })
                  }
                />
              </Field>
              <Field label="Company">
                <Input
                  value={form.companyName}
                  onChange={(e) =>
                    setForm({ ...form, companyName: e.target.value })
                  }
                />
              </Field>
              <Field label="Email">
                <Input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                />
              </Field>
              <Field label="Phone">
                <Input
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                />
              </Field>
              <Field label="Source">
                <Select
                  value={form.source}
                  onChange={(e) => setForm({ ...form, source: e.target.value })}
                >
                  {SOURCES.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field
                label="Estimated value"
                hint="Rough deal size, used to size the funnel."
              >
                <Input
                  inputMode="decimal"
                  value={form.estimatedValue}
                  onChange={(e) =>
                    setForm({ ...form, estimatedValue: e.target.value })
                  }
                />
              </Field>
              <Field label={t("money.currency")} hint={t("money.currencyHint")}>
                <Select
                  value={form.currencyCode}
                  onChange={(e) =>
                    setForm({ ...form, currencyCode: e.target.value })
                  }
                >
                  {!form.currencyCode && (
                    <option value="">{t("money.chooseCurrency")}</option>
                  )}
                  {currencies.map((c) => (
                    <option key={c.value} value={c.value}>
                      {c.label}
                    </option>
                  ))}
                </Select>
              </Field>
            </div>
            {create.error && (
              <p style={{ color: "var(--atlas-danger)", fontSize: 13 }}>
                {create.error.message}
              </p>
            )}
            <Button type="submit" variant="primary" disabled={create.isPending}>
              {create.isPending ? "Saving…" : "Create lead"}
            </Button>
          </form>
        </Card>
      )}

      {isLoading ? (
        <Skeleton rows={8} />
      ) : error ? (
        <EmptyState title="Could not load leads" message={error.message} />
      ) : rows.length === 0 ? (
        <EmptyState
          title="No leads yet"
          message="Leads arrive from web forms, events and outbound work. Add one by hand to see how qualification flows through to opportunities."
          action={
            <Button
              variant="primary"
              onClick={() => {
                setForm(blank());
                setCreating(true);
              }}
            >
              New lead
            </Button>
          }
        />
      ) : (
        <Card title={`Leads (${data?.total ?? rows.length})`}>
          <DataTable<LeadRow>
            rows={rows}
            columns={[
              {
                key: "name",
                label: "Name",
                render: (r) => personName(r.first_name, r.last_name),
              },
              {
                key: "company_name",
                label: "Company",
                render: (r) => r.company_name ?? "—",
              },
              { key: "email", label: "Email", render: (r) => r.email ?? "—" },
              {
                key: "source",
                label: "Source",
                render: (r) => r.source ?? "—",
              },
              {
                key: "estimated_value",
                label: "Estimated",
                numeric: true,
                render: (r) =>
                  r.estimated_value
                    ? formatMoney(r.estimated_value, r.currency_code)
                    : "—",
              },
              {
                key: "owner_user_id",
                label: "Owner",
                render: (r) =>
                  r.owner_user_id ? (
                    "Assigned"
                  ) : (
                    <span style={{ color: "var(--atlas-warning)" }}>
                      Unassigned
                    </span>
                  ),
              },
              {
                key: "status",
                label: "Status",
                render: (r) => <StatusBadge status={r.status} />,
              },
            ]}
          />
        </Card>
      )}
    </div>
  );
}
